// @flow
import React, { Component, Fragment } from 'react';
import update from 'immutability-helper';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import classNames from 'classnames/bind';
import Cleave from 'cleave.js/dist/cleave-react';
import Modal from '../../../../elements/Modal/Modal';
import Loading from '../../../../elements/Loading/Loading';
import { endpoints, searchAdmin1Api, searchAdmin2Api, searchAdmin3Api } from '../../../preferences/saved-addresses/api';
import { MyAddress } from './DeliveryMethods/MyAddress/MyAddress';
import { BillingAddress } from './BillingAddress/BillingAddress';
import Select from '../../../../elements/Select/Select';
import { DEFAULT_COUNTRIES, getLocalizedCountry } from '../../../local-values/utils/countries';
import PaymentMethod from './PaymentMethod/PaymentMethod';
import { getImplementation } from '../PaymentImpl';
import { groupItems } from '../../QuoteStep/utils';
import { animateScroll as scroll, scroller } from 'react-scroll';
import { Element } from 'react-scroll/modules';
import analytics from '../../../../analytics';
import { getAddressFormValidator } from '../../../../utils/address';
import Summary, { SummaryRow, SummaryRule } from '../../../../elements/Summary/Summary';
import FinalPrice from '../../QuoteStep/components/FinalPrice/FinalPrice';
import Coupon from '../../QuoteStep/components/Coupon/Coupon';

import style from './CheckoutStep.scss';
import formStyle from '../../../../assets/styles/form.scss';
import buttonStyle from '../../../../assets/styles/button.scss';

import type { Address, Admin, Bag, BagItem, Country, Order, ShoppingCartItem } from '../../../../entities';
import type { LocaleState } from '../../../locale/duck';
import type { Option } from '../../../../elements/Select/Select';
import type { LocalValuesState } from '../../../local-values/duck';
import type { BillingAddressReq, CardOrderAndTokenReq, PaymentExecuteReq, UpdateOrderPaymentMethodReq } from '../api';
import type { HTTPError } from '../../../../error';
import type { Match } from '../../../../router';
import type { PaymentImpl } from '../PaymentImpl';
import type { RootState } from '../../../index';
import type { FormValidationResult } from '../../../../utils/form';
import type { Forecast, ForecastCourier, ForecastTier, ForecastType } from '../../QuoteStep/api';
import type { ItemsByStore } from '../../QuoteStep/components/QuoteSummary/QuoteSummary';
import type { PaymentExecuteUIError } from '../duck';

type Item = BagItem | ShoppingCartItem;

type Props = {
  +dispatch: (action: any) => void;
  +rootState: RootState;
  +match: Match;
  +location: Location;
  +locale: LocaleState;
  +localValues: LocalValuesState;
  +fetchAddresses: () => void;
  +addresses?: Address[];
  +isFetchingAddresses?: boolean;
  +fetchAddressesError?: HTTPError;
  +paymentMethods?: { [key: string]: string };
  +isFetchingPaymentMethods?: boolean;
  +fetchPaymentMethodsError?: HTTPError;
  +fetchPaymentMethods: (country: Country) => void;
  +order: Order;
  +isFetchingOrder?: boolean;
  +fetchOrderError?: HTTPError;
  +isCreatingOrderAndToken: boolean;
  +createOrderAndTokenError?: HTTPError;
  +isUpdatingOrderPaymentMethod: boolean;
  +updateOrderPaymentMethodError?: HTTPError;
  +createCartOrderAndToken: (cardOrderAndTokenReq: CardOrderAndTokenReq) => void;
  +createBagOrderAndToken: (cardOrderAndTokenReq: CardOrderAndTokenReq, bagId?: string) => void;
  +updateOrderPaymentMethod: (updateOrderPaymentMethodReq: UpdateOrderPaymentMethodReq, orderId: string) => void;
  +fetchOrder: (orderId: string) => void;
  +goToRedirect: (route: string) => void;
  +bag?: Bag;
  +isPostingPaymentExecute?: boolean;
  +paymentExecuteSuccess?: boolean;
  +paymentExecuteError?: HTTPError;
  +postPaymentExecute: (paymentExecuteReq: PaymentExecuteReq, paymentId: string) => void;
  +resetState: () => void;
  +isFetchingCart: boolean;
  +shoppingCart?: ShoppingCartItem[];
  +importMethod?: ForecastType;
  +selectedIndex?: number;
  +forecast?: Forecast;
  +appliedCoupon?: string;
  +removeAppliedCoupon: () => void;
  +paymentExecuteUIError?: PaymentExecuteUIError;
  +paymentGetLibraryError?: PaymentExecuteUIError;
  +resetAlertState: () => void;
  +postPaymentExecuteSuccess: (order: Order) => void;
  +applyCoupon: (couponCode: string) => void;
};

type State = {
  deliveryAddress: {
    admin1: {
      value: string;
      label?: string;
      errors?: string[];
    };
    admin1Items: Admin[];
    isLoadingAdmin1?: boolean;
    admin2: {
      value: string;
      label?: string;
      errors?: string[];
    };
    admin2Items: Admin[];
    isLoadingAdmin2?: boolean;
    admin3: {
      value: string;
      label?: string;
      errors?: string[];
    };
    admin3Items: Admin[];
    isLoadingAdmin3?: boolean;
    address1: {
      value: string;
      errors?: string[];
    };
    address2: {
      value: string;
      errors?: string[];
    };
    firstName: {
      value: string;
      errors?: string[];
    };
    idNumber: {
      value: string;
      errors?: string[];
    };
    lastName: {
      value: string;
      errors?: string[];
    };
    name: {
      value: string;
      errors?: string[];
    };
    phone: {
      value: string;
      errors?: string[];
    };
    zipCode: {
      value: string;
      errors?: string[];
    };
    saveAddress: {
      value: boolean;
    };
  };
  addressOption: {
    value: string;
    errors?: string[];
  };
  billingAddress: {
    admin1: {
      value: string;
      label?: string;
      errors?: string[];
    };
    admin1Items: Admin[];
    isLoadingAdmin1?: boolean;
    admin2: {
      value: string;
      label?: string;
      errors?: string[];
    };
    admin2Items: Admin[];
    isLoadingAdmin2?: boolean;
    admin3: {
      value: string;
      label?: string;
      errors?: string[];
    };
    admin3Items: Admin[];
    isLoadingAdmin3?: boolean;
    address1: {
      value: string;
      errors?: string[];
    };
    address2: {
      value: string;
      errors?: string[];
    };
    firstName: {
      value: string;
      errors?: string[];
    };
    lastName: {
      value: string;
      errors?: string[];
    };
    idNumber: {
      value: string;
      errors?: string[];
    };
    name: {
      value: string;
      errors?: string[];
    };
    phone: {
      value: string;
      errors?: string[];
    };
    zipCode: {
      value: string;
      errors?: string[];
    };
    useSameAddress: {
      value: boolean;
    };
  };
  billingCountry: {
    value: Country;
    errors?: string[];
  };
  paymentMethod: {
    value: string;
    impl?: string;
  };
  paymentCaptureFailedModalOpen: boolean;
  auxRender: boolean;
  acceptTerms: boolean;
  niubizLoading: boolean;
};

const SCROLL_OPTIONS = {
  duration: 350,
  delay: 0,
  smooth: 'easeInOutQuart',
  offset: -100,
};

const messages = defineMessages({
  countryPlaceholder: {
    id: 'address.country.placeholder',
  },
  idPlaceholder: {
    id: 'address.idNumber.placeholder',
  },
  phonePlaceholder: {
    id: 'address.phone.placeholder',
  },
});

const EMPTY_BILLING_ADDRESS = {
  admin1: { value: '', name: '' },
  admin2: { value: '', name: '' },
  admin3: { value: '', name: '' },
  admin1Items: [],
  admin2Items: [],
  admin3Items: [],
  isLoadingAdmin1: false,
  isLoadingAdmin2: false,
  isLoadingAdmin3: false,
  address1: { value: '' },
  address2: { value: '' },
  firstName: { value: '' },
  lastName: { value: '' },
  idNumber: { value: '' },
  name: { value: '' },
  phone: { value: '' },
  zipCode: { value: '' },
  useSameAddress: { value: true },
};

export default class CheckoutStep extends Component<Props, State> {
  newAddressPhoneCleave: Cleave;
  billingAddressPhoneCleave: Cleave;
  paymentImplementation: ?PaymentImpl;
  pageMode: 'order' | 'bag' | 'cart';
  quoteType: 'bag' | 'cart' | 'offline-cart' | 'offline-bag';

  constructor(props: Props) {
    super(props);
    const { match } = props;
    // Note: get pageMode from url '/new-order/checkout/[order | cart | bag]/...'
    // this.pageMode = match.url.split('/')[3] || '';
    const splitted = props.location.pathname.split('/');
    if (splitted[3] === 'bag') {
      this.pageMode = 'bag';
    } else if (splitted[3] === 'cart') {
      this.pageMode = 'cart';
    } else if (splitted[3] === 'order') {
      this.pageMode = 'order';
    }

    this.state = {
      deliveryAddress: {
        admin1: { value: '' },
        admin2: { value: '' },
        admin3: { value: '' },
        admin1Items: [],
        admin2Items: [],
        admin3Items: [],
        isLoadingAdmin1: true,
        isLoadingAdmin2: false,
        isLoadingAdmin3: false,
        address1: { value: '' },
        address2: { value: '' },
        firstName: { value: '' },
        idNumber: { value: '' },
        lastName: { value: '' },
        name: { value: '' },
        phone: { value: '' },
        zipCode: { value: '' },
        saveAddress: { value: false },
      },
      addressOption: { value: '' },
      billingAddress: {
        admin1: { value: '' },
        admin2: { value: '' },
        admin3: { value: '' },
        admin1Items: [],
        admin2Items: [],
        admin3Items: [],
        isLoadingAdmin1: true,
        isLoadingAdmin2: false,
        isLoadingAdmin3: false,
        address1: { value: '' },
        address2: { value: '' },
        firstName: { value: '' },
        idNumber: { value: '' },
        lastName: { value: '' },
        name: { value: '' },
        phone: { value: '' },
        zipCode: { value: '' },
        useSameAddress: { value: true },
      },
      billingCountry: {
        value: '',
      },
      paymentMethod: {
        value: '',
        impl: '',
      },
      paymentCaptureFailedModalOpen: false,
      auxRender: true,
      acceptTerms: true,
      niubizLoading: false,
    };
  }

  componentWillUnmount() {
    const { resetState, resetAlertState } = this.props;
    resetState();
    resetAlertState();
  }

  componentDidMount() {
    const {
      locale, fetchAddresses, addresses, bag, match, fetchOrder, selectedIndex,
      forecast, goToRedirect, order, fetchPaymentMethods,
    } = this.props;
    const { orderId } = match.params || {};

    // Fire checkout reached
    if (this.pageMode === 'order' && !!order) {
      analytics.fireCustomEvent('Checkout Step Viewed', {
        category: 'Checkout Step',
        action: 'Viewed',
        label: order.id,
        user_type: 'purchaser',
        user_referrer: window.localStorage.getItem('userReferrer') || '',
        landing_url: window.localStorage.getItem('landingUrl') || '',
        order_id: orderId || '',
        order_formatted_id: orderId || '',
        payment_method: order.invoice && order.invoice.payment ? order.invoice.payment.method : '',
        payment_sub_method: order.invoice && order.invoice.payment ? order.invoice.payment.subtype : '',
        domain_country: locale.domainCountry,
        type: 'order',
        step: 1,
        shipping_method: order.serviceTier,
      });
    } else if (this.pageMode !== 'order') {
      analytics.fireCustomEvent('Checkout Step Viewed', {
        category: 'Checkout Step',
        action: 'Viewed',
        user_type: 'purchaser',
        user_referrer: window.localStorage.getItem('userReferrer') || '',
        landing_url: window.localStorage.getItem('landingUrl') || '',
        order_id: '',
        order_formatted_id: '',
        payment_method: '',
        payment_sub_method: '',
        domain_country: locale.domainCountry,
        type: this.pageMode,
        step: 0,
        shipping_method: '',
      });
    }

    if ((typeof selectedIndex !== 'number' || !forecast) && this.pageMode !== 'order') {
      if (this.pageMode === 'bag' && !bag) {
        const bagId = match.url.split('/')[4] || '';
        // return goToRedirect(`/new-order/quote/bag/${bagId}`);
        return goToRedirect('/');
      } else if (this.pageMode === 'cart') {
        // return goToRedirect('/new-order/quote/cart');
        return goToRedirect('/');
      }
    }

    if (order && order.shippingAddress && order.shippingAddress.country &&
      order.shippingAddress.country.code === locale.domainCountry) {
      this.setState({
        addressOption: {
          value: "new-address",
        },
      });
      this.populateNewAddressForm(order.shippingAddress);
    }

    if (!addresses) {
      fetchAddresses();
    }

    if (this.pageMode === 'order' && !order && orderId) {
      fetchOrder(orderId);
    }

    if (locale.domainCountry) {
      const country: Country = locale.domainCountry
      fetchPaymentMethods(country);
      this.setState({
        billingCountry: {
          value: country,
          isLoadingAdmin1: true,
        },
      }, () => searchAdmin1Api(undefined, {
        route: endpoints.SEARCH_ADMIN1.replace(':country', country),
      }).toPromise().then((admin1Items: Location[]) =>
        this.setState((nextState: State) => update(nextState, {
          billingAddress: {
            $set: update(nextState.billingAddress, {
              admin1Items: { $set: admin1Items },
              isLoadingAdmin1: { $set: false },
            }),
          },
          deliveryAddress: {
            $set: update(nextState.deliveryAddress, {
              admin1Items: { $set: admin1Items },
              isLoadingAdmin1: { $set: false },
            }),
          },
        }))));
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const {
      order, goToRedirect, match, dispatch, rootState, fetchPaymentMethodsError, locale,
      createOrderAndTokenError, paymentExecuteError, updateOrderPaymentMethodError,
      paymentGetLibraryError, removeAppliedCoupon, postPaymentExecuteSuccess,
    } = this.props;

    const { paymentMethod } = this.state;

    const orderIdParam = match.params.orderId;

    if (!prevProps.order && !!order) {
      analytics.fireCustomEvent('Checkout Step Viewed', {
        category: 'Checkout Step',
        action: 'Viewed',
        label: order.id,
        user_type: 'purchaser',
        user_referrer: window.localStorage.getItem('userReferrer') || '',
        landing_url: window.localStorage.getItem('landingUrl') || '',
        order_id: order.id || '',
        order_formatted_id: order.id || '',
        payment_method: order.invoice && order.invoice.payment ? order.invoice.payment.method : '',
        payment_sub_method: order.invoice && order.invoice.payment ? order.invoice.payment.subtype : '',
        domain_country: locale.domainCountry,
        type: 'order',
        step: 1,
        shipping_method: order.serviceTier,
      });

      if (order && order.shippingAddress && order.shippingAddress.country &&
        order.shippingAddress.country.code === locale.domainCountry) {
        if (this.pageMode === "order") {
          this.setState({
            addressOption: {
              value: "new-address",
            },
          });
        }
        this.populateNewAddressForm(order.shippingAddress);
      }

      if (this.paymentImplementation) {
        this.paymentImplementation && this.paymentImplementation.setUpPayment(order, dispatch, rootState);
      }
      // TODO: check only order ids
      if (orderIdParam !== order.id) {
        this.pageMode = 'order';
        goToRedirect(`/new-order/checkout/order/${order.id}`);
        // NOTE: Here we check if the order arrives with an status that means that it has been already paid
        // So it can be handled the the executeSuccess sequence.
        if (order.state === 'AVAILABLE' || order.state === 'AVAILABLE_FOR_COURIER') {
          return postPaymentExecuteSuccess(order);
        }
      }
    }

    // If the payment id has changed or the previous payment is null because it has expired
    if (!!order && !!prevProps.order && !!order.invoice && !!prevProps.order.invoice &&
      ((!!order.invoice.payment && !!prevProps.order.invoice.payment &&
        (order.invoice.payment.id !== prevProps.order.invoice.payment.id)) ||
        (!!order.invoice.payment && !prevProps.order.invoice.payment))) {
      if (this.paymentImplementation && this.paymentImplementation.type !== (paymentMethod.impl && paymentMethod.impl.toLowerCase())) {
        this.paymentImplementation.clean();
      }

      const paymentImplementation = getImplementation(order.invoice.payment.method);

      if (!!paymentImplementation) {
        paymentImplementation.setUpPayment(order, dispatch, rootState);
        this.paymentImplementation = paymentImplementation;
        // Since generateCustomUI on a payment implementation just returns a Node
        // this line is used to force a render and therefore a generateCustomUI call, 
        // after setUpPayment has been executed.
        this.setState((st: State) => ({ auxRender: !st.auxRender }));
      }
    }

    // If there's some error then scroll to top in order to see the alert
    if ((!prevProps.fetchPaymentMethodsError && fetchPaymentMethodsError) ||
      (!prevProps.createOrderAndTokenError && createOrderAndTokenError) ||
      (!prevProps.updateOrderPaymentMethodError && updateOrderPaymentMethodError) ||
      (!prevProps.paymentExecuteError && paymentExecuteError &&
        (!paymentExecuteError.data || (paymentExecuteError.data &&
          paymentExecuteError.data.name !== 'PaymentCaptureFailed'))) ||
      (!prevProps.paymentGetLibraryError && paymentGetLibraryError)) {
      scroll.scrollTo(0, {
        duration: 400,
      })
    }

    // If there's the PaymentCaptureFailed then show the modal
    if (!prevProps.paymentExecuteError && paymentExecuteError &&
      paymentExecuteError.data && paymentExecuteError.data.name === 'PaymentCaptureFailed') {
      this.setState({
        paymentCaptureFailedModalOpen: true,
      });
    }

    if (!prevProps.createOrderAndTokenError && createOrderAndTokenError && !!createOrderAndTokenError.data &&
      createOrderAndTokenError.name === 'CouponNotFoundOrInvalidError') {
      removeAppliedCoupon();
    }

    if (!prevProps.locale.domainCountry && locale.domainCountry) {
      this.handleBillingAddressChangeCountry({ value: locale.domainCountry });
    }

    /*
    const { value } = prevState.deliveryMethod;
    if (value !== this.state.deliveryMethod.value) {
      this.setState((state: State) => update(state, {
        billingAddress: {
          useSameAddress: {
            value: { $set: false },
          },
        },
      }));
    }
    */
  }

  /*handleChangeDeliveryMethod = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;
      this.setState((state: State) => update(state, {
        deliveryMethod: {
          value: { $set: value },
        },
        billingAddress: {
          useSameAddress: {
            value: { $set: false },
          },
        },
      }));
    }
  };*/

  paymentMethodHasBillingAddress(paymentImpl: string) {
    return paymentImpl === 'BLUESNAP' || paymentImpl === 'CULQI';
  }

  /* New Address  */
  handleNewAddressPhoneInit = (cleave: any) => {
    this.newAddressPhoneCleave = cleave;
  };

  handleNewAddressChangeMyAddresses = (option: Option) => {
    const { addresses } = this.props;
    if (typeof option.value === 'string') {
      this.setState({
        addressOption: {
          value: option.value,
        },
      });
    }
    if (option.value === 'new-address') {
      this.clearNewAddressForm();
    } else if (typeof option.value === 'string') {
      if (addresses) {
        const myAddress = addresses.find((address: Address) => address.id === option.value);
        this.populateNewAddressForm(myAddress);
      }
    }
  };

  clearNewAddressForm = () => {
    this.setState((state: State) => update(state, {
      deliveryAddress: {
        $set: update(state.deliveryAddress, {
          admin1: { $set: { value: '' } },
          admin2: { $set: { value: '' } },
          admin3: { $set: { value: '' } },
          $unset: ['admin2Items', 'admin3Items'],
          isLoadingAdmin1: { $set: false },
          isLoadingAdmin2: { $set: false },
          isLoadingAdmin3: { $set: false },
          address1: { $set: { value: '' } },
          address2: { $set: { value: '' } },
          firstName: { $set: { value: '' } },
          idNumber: { $set: { value: '' } },
          lastName: { $set: { value: '' } },
          name: { $set: { value: '' } },
          phone: { $set: { value: '' } },
          zipCode: { $set: { value: '' } },
          saveAddress: { $set: { value: false } },
        }),
      },
    }));
  };

  clearBillingAddressForm = () => {
    this.setState((state: State) => update(state, {
      billingAddress: {
        $set: update(state.billingAddress, {
          admin1: { $set: { value: '' } },
          admin2: { $set: { value: '' } },
          admin3: { $set: { value: '' } },
          $unset: ['admin2Items', 'admin3Items'],
          isLoadingAdmin1: { $set: false },
          isLoadingAdmin2: { $set: false },
          isLoadingAdmin3: { $set: false },
          address1: { $set: { value: '' } },
          address2: { $set: { value: '' } },
          firstName: { $set: { value: '' } },
          idNumber: { $set: { value: '' } },
          lastName: { $set: { value: '' } },
          name: { $set: { value: '' } },
          phone: { $set: { value: '' } },
          zipCode: { $set: { value: '' } },
          saveAddress: { $set: { value: false } },
        }),
      },
    }));
  };

  populateNewAddressForm = (myAddress: Address) => {

    this.setState((state: State) => update(state, {
        deliveryAddress: {
          $set: update(state.deliveryAddress, {
            address1: { $set: { value: myAddress.address1 } },
            address2: { $set: { value: myAddress.address2 || '' } },
            admin1: { $set: { value: myAddress.admin1.id, label: myAddress.admin1.name } },
            admin2: { $set: { value: myAddress.admin2.id, label: myAddress.admin2.name } },
            admin3: {
              $set: {
                value: myAddress.admin3 ? myAddress.admin3.id : '',
                label: myAddress.admin3 ? myAddress.admin3.name : '',
              },
            },
            firstName: { $set: { value: myAddress.firstName } },
            idNumber: { $set: { value: myAddress.idNumber } },
            lastName: { $set: { value: myAddress.lastName } },
            phone: { $set: { value: myAddress.phone } },
            zipCode: { $set: { value: myAddress.zipCode } },
          }),
        },
      }), () =>
        this.handleNewAddressChangeAdmin1({
            value: myAddress.admin1.id,
            label: myAddress.admin1.name,
          }, () => this.handleNewAddressChangeAdmin2({
            value: myAddress.admin2.id,
            label: myAddress.admin2.name,
          }, () => {
            if (myAddress.admin3) {
              this.handleNewAddressChangeAdmin3({
                value: myAddress.admin3.id,
                label: myAddress.admin3.name,
              });
            }
          }),
        ),
    );
  };

  handleNewAddressChangeAdmin1 = (option: Option, afterSetStateCb?: () => void) => {
    const { locale } = this.props;
    if (!option) return console.log('option is not defined');
    if (!(typeof option.value === 'number'))
      return console.log('Option value not number!');
    this.setState((state: State) => update(state, {
      deliveryAddress: {
        $set: update(state.deliveryAddress, {
          admin1: { $set: { value: `${option.value}`, label: option.label || '' } },
          admin2: { $set: { value: '', label: '' } },
          admin2Items: { $set: [] },
          isLoadingAdmin2: { $set: true },
          admin3: { $set: { value: '', label: '' } },
          admin3Items: { $set: undefined },
        }),
      },
    }), () => {
      if (!!afterSetStateCb) {
        afterSetStateCb();
      }
      if (locale.domainCountry) {
        searchAdmin2Api(undefined, {
          route: endpoints.SEARCH_ADMIN2
            .replace(':country', locale.domainCountry)
            .replace(':admin1', `${option.value}`),
        }).toPromise()
          .then((admin2Items: Location[]) =>
            this.setState((nextState: State) => update(nextState, {
                deliveryAddress: {
                  $set: update(nextState.deliveryAddress, {
                    admin2Items: { $set: admin2Items },
                    isLoadingAdmin2: { $set: false },
                  }),
                },
              },
            )),
          );
      }
    });
  };

  handleNewAddressChangeAdmin2 = (option: Option, afterSetStateCb?: () => void) => {
    const { locale } = this.props;
    const { deliveryAddress } = this.state;
    if (!option) return console.log('option is not defined');
    if (!(typeof option.value === 'number'))
      return console.log('Option value not number!');
    this.setState((state: State) => update(state, {
      deliveryAddress: {
        $set: update(state.deliveryAddress, {
          admin2: { $set: { value: `${option.value}`, label: option.label || '' } },
          isLoadingAdmin3: { $set: true },
          admin3: { $set: { value: '', label: '' } },
          admin3Items: { $set: undefined },
        }),
      },
    }), () => {
      if (!!afterSetStateCb) {
        afterSetStateCb();
      }
      if (locale.domainCountry && locale.domainCountry !== 'US') {
        searchAdmin3Api(undefined, {
          route: endpoints.SEARCH_ADMIN3
            .replace(':country', locale.domainCountry)
            .replace(':admin1', deliveryAddress.admin1.value)
            .replace(':admin2', `${option.value}`),
        }).toPromise().then((admin3Items: Location[]) =>
          this.setState((nextState: State) => update(nextState, {
            deliveryAddress: {
              $set: update(nextState.deliveryAddress, {
                admin3Items: { $set: admin3Items },
                isLoadingAdmin3: { $set: false },
              }),
            },
          })));
      }
    });
  };

  handleNewAddressChangeAdmin3 = (option: Option) => {
    if (!option) return console.log('option is not defined');
    if (!(typeof option.value === 'number'))
      return console.log('Option value not number!');
    this.setState((state: State) => update(state, {
      deliveryAddress: {
        $set: update(state.deliveryAddress, {
          admin3: { $set: { value: `${option.value}`, label: option.label || '' } },
        }),
      },
    }));
  };

  handleNewAddressChange = (e: SyntheticInputEvent<HTMLInputElement>) => {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState((state: State) => update(state, {
        deliveryAddress: {
          $set: update(state.deliveryAddress, {
            [name]: { $set: { value } },
          }),
        },
      }));
    }
  };

  handleNewAddressChangeSaveAddress = () => {
    this.setState((state: State) => update(state, {
      deliveryAddress: {
        $set: update(state.deliveryAddress, {
          saveAddress: { $set: { value: !state.deliveryAddress.saveAddress.value } },
        }),
      },
    }));
  };

  handleNewAddressChangePhone = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      // $FlowIgnoreLine: raw value not in target unless Cleave makes a flowtyped (or the community)
      const { value, rawValue } = e.target;
      if (value.length <= 15) {
        this.setState((state: State) => update(state, {
          deliveryAddress: {
            $set: update(state.deliveryAddress, {
              phone: { $set: { value: rawValue } },
            }),
          },
        }));
        this.newAddressPhoneCleave.setRawValue(rawValue);
      }
      if (rawValue.length > 15) {
        this.newAddressPhoneCleave.setRawValue(this.state.deliveryAddress.phone.value);
      }
    }
  };

  handleNewAddressFocus = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      // $FlowIgnoreLine: raw value not in target unless Cleave makes a flowtyped (or the community)
      const { name, value, rawValue } = e.target;
      if (name === 'phone') {
        if (value.length <= 15) {
          this.setState((state: State) => update(state, {
            deliveryAddress: {
              $set: update(state.deliveryAddress, {
                phone: { $set: { value: rawValue } },
              }),
            },
          }));
          this.newAddressPhoneCleave.setRawValue(rawValue);
        }
        if (rawValue.length > 15) {
          this.newAddressPhoneCleave.setRawValue(this.state.deliveryAddress.phone.value);
        }
      } else {
        this.setState((state: State) => update(state, {
          deliveryAddress: {
            $set: update(state.deliveryAddress, {
              [name]: { $set: { value } },
            }),
          },
        }));
      }
    }
  };

  /* Billing Address */
  handleBillingAddressPhoneInit = (cleave: any) => {
    this.billingAddressPhoneCleave = cleave;
  };

  handleBillingAddressUseSameAddress = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      this.clearBillingAddressForm();
      this.setState((state: State) => update(state, {
        billingAddress: {
          $set: update(state.billingAddress, {
            useSameAddress: { $set: { value: !state.billingAddress.useSameAddress.value } },
          }),
        },
      }));
    }
  };

  handleBillingAddressChangeAdmin1 = (option: Option) => {
    if (!option) return console.log('option is not defined');
    if (!(typeof option.value === 'number'))
      return console.log('Option value not number!');
    this.setState((state: State) => update(state, {
      billingAddress: {
        $set: update(state.billingAddress, {
          admin1: { $set: { value: `${option.value}`, label: option.label || '' } },
          admin2: { $set: { value: '', label: '' } },
          admin2Items: { $set: [] },
          isLoadingAdmin2: { $set: true },
          admin3: { $set: { value: '', label: '' } },
          admin3Items: { $set: undefined },
        }),
      },
    }), () => {
      const { billingCountry } = this.state;
      if (billingCountry.value) {
        searchAdmin2Api(undefined, {
          route: endpoints.SEARCH_ADMIN2
            .replace(':country', billingCountry.value)
            .replace(':admin1', `${option.value}`),
        }).toPromise().then((admin2Items: Location[]) =>
          this.setState((nextState: State) => update(nextState, {
            billingAddress: {
              $set: update(nextState.billingAddress, {
                admin2Items: { $set: admin2Items },
                isLoadingAdmin2: { $set: false },
              }),
            },
          })));
      }
    });
  };

  handleBillingAddressChangeAdmin2 = (option: Option) => {
    const { billingAddress } = this.state;
    if (!option) return console.log('option is not defined');
    if (!(typeof option.value === 'number'))
      return console.log('Option value not number!');
    this.setState((state: State) => update(state, {
      billingAddress: {
        $set: update(state.billingAddress, {
          admin2: { $set: { value: `${option.value}`, label: option.label || '' } },
          isLoadingAdmin3: { $set: true },
          admin3: { $set: { value: '', label: '' } },
          admin3Items: { $set: undefined },
        }),
      },
    }), () => {
      const { billingCountry } = this.state;
      if (billingCountry.value && billingCountry.value !== 'US') {
        searchAdmin3Api(undefined, {
          route: endpoints.SEARCH_ADMIN3
            .replace(':country', billingCountry.value)
            .replace(':admin1', billingAddress.admin1.value)
            .replace(':admin2', `${option.value}`),
        }).toPromise().then((admin3Items: Location[]) =>
          this.setState((nextState: State) => update(nextState, {
            billingAddress: {
              $set: update(nextState.billingAddress, {
                admin3Items: { $set: admin3Items },
                isLoadingAdmin3: { $set: false },
              }),
            },
          })));
      }
    });
  };

  handleBillingAddressChangeAdmin3 = (option: Option) => {
    if (!option) return console.log('option is not defined');
    if (!(typeof option.value === 'number'))
      return console.log('Option value not number!');
    this.setState((state: State) => update(state, {
      billingAddress: {
        $set: update(state.billingAddress, {
          admin3: { $set: { value: `${option.value}`, label: option.label || '' } },
        }),
      },
    }));
  };

  handleBillingAddressChange = (e: SyntheticInputEvent<HTMLInputElement>) => {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState((state: State) => update(state, {
        billingAddress: {
          $set: update(state.billingAddress, {
            [name]: { $set: { value } },
          }),
        },
      }));
    }
  };

  handleBillingAddressChangePhone = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      // $FlowIgnoreLine: raw value not in target unless Cleave makes a flowtyped (or the community)
      const { value, rawValue } = e.target;
      if (value.length <= 15) {
        this.setState((state: State) => update(state, {
          billingAddress: {
            $set: update(state.billingAddress, {
              phone: { $set: { value: rawValue } },
            }),
          },
        }));
        this.billingAddressPhoneCleave.setRawValue(rawValue);
      }
      if (rawValue.length > 15) {
        this.billingAddressPhoneCleave.setRawValue(this.state.billingAddress.phone.value);
      }
    }
  };

  handleBillingAddressFocus = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      // $FlowIgnoreLine: raw value not in target unless Cleave makes a flowtyped (or the community)
      const { name, value, rawValue } = e.target;
      if (name === 'phone') {
        if (value.length <= 15) {
          this.setState((state: State) => update(state, {
            billingAddress: {
              $set: update(state.billingAddress, {
                phone: { $set: { value: rawValue } },
              }),
            },
          }));
          this.billingAddressPhoneCleave.setRawValue(rawValue);
        }
        if (rawValue.length > 15) {
          this.billingAddressPhoneCleave.setRawValue(this.state.billingAddress.phone.value);
        }
      } else {
        this.setState((state: State) => update(state, {
          billingAddress: {
            $set: update(state.billingAddress, {
              [name]: { $set: { value } },
            }),
          },
        }));
      }
    }
  };

  handleBillingAddressChangeCountry = (option: Option) => {
    const { fetchPaymentMethods } = this.props;
    const { billingCountry } = this.state;
    const country: Country = `${option.value}`;
    if (billingCountry.value !== country) {

      if (this.paymentImplementation) {
        this.paymentImplementation.clean();
        this.paymentImplementation = null;
      }

      this.setState((state: State) => update(state, {
        billingAddress: {
          $set: {
            ...EMPTY_BILLING_ADDRESS,
            admin1Items: state.billingAddress.admin1Items,
            isLoadingAdmin1: false,
            useSameAddress: { value: true },
          },
        },
      }));
      fetchPaymentMethods(country);
      this.setState({
        billingCountry: {
          value: country,
          isLoadingAdmin1: true,
        },
      }, () => searchAdmin1Api(undefined, {
        route: endpoints.SEARCH_ADMIN1.replace(':country', `${country}`),
      }).toPromise().then((admin1Items: Location[]) =>
        this.setState((nextState: State) => update(nextState, {
          billingAddress: {
            $set: update(nextState.billingAddress, {
              admin1Items: { $set: admin1Items },
              isLoadingAdmin1: { $set: false },
              useSameAddress: { value: true },
            }),
          },
          paymentMethod: {
            $set: {
              value: '',
              impl: '',
            },
          },
        }))));
    }
  };

  buildBillingAddressReq(): ?BillingAddressReq {
    const { billingAddress, billingCountry, deliveryAddress } = this.state;
    const { locale } = this.props;
    let address2 = !!billingAddress.address2.value ? { address2: billingAddress.address2.value } : undefined;
    let admin3Id = !!billingAddress.admin3.value ? { admin3Id: parseInt(billingAddress.admin3.value, 10) } : undefined;
    let zipCode = !!billingAddress.zipCode.value ? { zipCode: billingAddress.zipCode.value } : undefined;
    let idNumber = !!billingAddress.idNumber.value ? { idNumber: billingAddress.idNumber.value } : undefined;
    let billingAddressReq: BillingAddressReq = {
      firstName: billingAddress.firstName.value,
      lastName: billingAddress.lastName.value,
      ...idNumber,
      address1: billingAddress.address1.value,
      admin1Id: parseInt(billingAddress.admin1.value, 10),
      admin2Id: parseInt(billingAddress.admin2.value, 10),
      ...admin3Id,
      ...zipCode,
      phone: billingAddress.phone.value,
      country: billingCountry.value,
      ...address2,
    };
    if (billingAddress.useSameAddress.value
      && billingCountry.value === locale.domainCountry) {
      address2 = !!deliveryAddress.address2.value ? { address2: deliveryAddress.address2.value } : undefined;
      admin3Id = !!deliveryAddress.admin3.value ? { admin3Id: parseInt(deliveryAddress.admin3.value, 10) } : undefined;
      zipCode = !!deliveryAddress.zipCode.value ? { zipCode: deliveryAddress.zipCode.value } : undefined;
      idNumber = !!deliveryAddress.idNumber.value ? { idNumber: deliveryAddress.idNumber.value } : undefined;
      billingAddressReq = {
        firstName: deliveryAddress.firstName.value,
        lastName: deliveryAddress.lastName.value,
        ...idNumber,
        address1: deliveryAddress.address1.value,
        admin1Id: parseInt(deliveryAddress.admin1.value, 10),
        ...admin3Id,
        admin2Id: parseInt(deliveryAddress.admin2.value, 10),
        ...zipCode,
        phone: deliveryAddress.phone.value,
        country: billingCountry.value,
        ...address2,
      };
    }
    return { ...billingAddressReq };
  }

  /*buildAddressMap() {
    const { locale } = this.props;
    let coordinates = {
      latitude: '',
      longitude: '',
      markerLatitude: '',
      markerLongitude: ''
    };
    switch (locale.domainCountry) {
      case 'PE':
        coordinates.latitude = '-12.092217603598858';
        coordinates.longitude = '-77.05843668864784';
        coordinates.markerLatitude = '-12.092217603598858';
        coordinates.markerLongitude = '-77.05843668864784';
        break;
      case 'CO':
        coordinates.latitude = '6.2095559';
        coordinates.longitude = '-75.5665177';
        coordinates.markerLatitude = '6.2095559';
        coordinates.markerLongitude = '-75.5665177';
        break;
    }
    /!*-12.1058802,-77.0203015*!/
    return <StaticMap center={`${coordinates.latitude},${coordinates.longitude}`}
                      zoom={16}
                      width={544}
                      height={300}
                      className={style.mapContainer}
                      marker={`${coordinates.markerLatitude},${coordinates.markerLongitude}`}/>
  }*/

  handleChangePaymentMethod = (value: string, name: string) => {
    const {
      createCartOrderAndToken, createBagOrderAndToken, updateOrderPaymentMethod,
      locale, order, bag, appliedCoupon,
    } = this.props;
    const { billingCountry } = this.state;
    this.setState((state: State) => ({
      paymentMethod: {
        value,
        impl: name,
      },
    }), () => {
      const { selectedIndex, importMethod, forecast } = this.props;
      // This remains here, there's no way to certain know the implName in componentDidUpdate
      if (this.paymentImplementation &&
        this.paymentImplementation.type !== (this.state.paymentMethod.impl && this.state.paymentMethod.impl.toLowerCase())) {
        this.paymentImplementation.clean();
      }
      this.paymentImplementation = getImplementation(name.toLowerCase());
      if (!order) {
        if (!forecast || typeof selectedIndex !== 'number') {
          return console.error(new Error('Either forecast or selectedIndex are not valid values'));
        }
        const req: any = {
          type: value,
          method: name,
          purchaseCountry: 'US',
          deliveryCountry: locale.domainCountry,
          billingCountry: billingCountry.value,
          shippingAddress: this.buildAddress(),
        };
        if (!!importMethod && importMethod === 'CLASSIC') {
          req.serviceTier = forecast['CLASSIC'][selectedIndex].tier.name;
        }
        if (!!importMethod && importMethod === 'DIY') {
          req.courierId = forecast['DIY'][selectedIndex].courier.id;
        }
        if (appliedCoupon) {
          req.couponCode = appliedCoupon;
        }
        const userReferrer = window.localStorage.getItem('userReferrer');
        const landingUrl = window.localStorage.getItem('landingUrl');
        if (userReferrer) {
          req.userReferrer = userReferrer;
        }
        if (landingUrl) {
          req.landingUrl = landingUrl;
        }
        if (this.pageMode === 'cart') {
          createCartOrderAndToken(req);
        } else if (this.pageMode === 'bag' && bag) {
          createBagOrderAndToken(req, bag.id);
        }
      } else if (order.invoice.state === 'PENDING') {
        updateOrderPaymentMethod({
          type: value,
          method: name,
          billingCountry: billingCountry.value,
        }, order.id);
      }
    });
  };

  // TODO: maybe rename function
  buildAddress = () => {
    const { addressOption, deliveryAddress } = this.state;
    if (addressOption.value !== 'new-address') {
      return {
        id: addressOption.value,
      };
    } else {
      const address2 = !!deliveryAddress.address2.value ? { address2: deliveryAddress.address2.value } : undefined;
      const admin3 = !!deliveryAddress.admin3.value ? { admin3Id: parseInt(deliveryAddress.admin3.value, 10) } : undefined;
      const zipCode = !!deliveryAddress.zipCode.value ? { zipCode: deliveryAddress.zipCode.value } : undefined;
      const name = !!deliveryAddress.name.value ? { name: deliveryAddress.name.value } : undefined;
      const idNumber = !!deliveryAddress.idNumber.value ? { idNumber: deliveryAddress.idNumber.value } : undefined;
      return {
        firstName: deliveryAddress.firstName.value,
        lastName: deliveryAddress.lastName.value,
        ...idNumber,
        address1: deliveryAddress.address1.value,
        admin1Id: parseInt(deliveryAddress.admin1.value, 10),
        admin2Id: parseInt(deliveryAddress.admin2.value, 10),
        ...admin3,
        phone: deliveryAddress.phone.value,
        ...address2,
        ...zipCode,
        ...name,
      };
    }
  };

  validateDeliveryAddressForm = (): FormValidationResult[] => {
    const { deliveryAddress } = this.state;
    const { locale } = this.props;

    return getAddressFormValidator(locale.domainCountry, 'myAddress').validateForm({
      admin1: deliveryAddress.admin1.value,
      admin2: deliveryAddress.admin2.value,
      admin3: deliveryAddress.admin3.value,
      address1: deliveryAddress.address1.value,
      firstName: deliveryAddress.firstName.value,
      lastName: deliveryAddress.lastName.value,
      idNumber: deliveryAddress.idNumber.value,
      //zipCode: deliveryAddress.zipCode.value,
      phone: deliveryAddress.phone.value,
    });
  };

  validateBillingAddressForm = (): FormValidationResult[] => {
    const { billingAddress, billingCountry } = this.state;
    const { locale } = this.props;

    return getAddressFormValidator(billingCountry.value, 'billing').validateForm({
      admin1: billingAddress.admin1.value,
      admin2: billingAddress.admin2.value,
      admin3: billingAddress.admin3.value,
      address1: billingAddress.address1.value,
      firstName: billingAddress.firstName.value,
      lastName: billingAddress.lastName.value,
      idNumber: billingAddress.idNumber.value,
      //zipCode: billingAddress.zipCode.value,
      phone: billingAddress.phone.value,
    });
  };

  handleConfirmPayment = () => {
    const { addressOption, paymentMethod, billingAddress } = this.state;
    const { rootState } = this.props;
    const errors: FormValidationResult[] = this.validateDeliveryAddressForm();
    const billingErrors: FormValidationResult[] = this.validateBillingAddressForm();
    if (addressOption.value === 'new-address' && errors.length) {
      errors.forEach((error: FormValidationResult) => {
        this.setState((state: State) => update(state, {
          deliveryAddress: {
            [error.field]: {
              $merge: {
                errors: error.errors,
              },
            },
          },
        }));
        scroller.scrollTo('newAddressForm', SCROLL_OPTIONS);
      });
    } else if (paymentMethod && (paymentMethod.impl === 'BLUESNAP' || paymentMethod.impl === 'CULQI') &&
      !billingAddress.useSameAddress.value && billingErrors.length) {
      billingErrors.forEach((error: FormValidationResult) => {
        this.setState((state: State) => update(state, {
          billingAddress: {
            [error.field]: {
              $merge: {
                errors: error.errors,
              },
            },
          },
        }));
      });
      scroller.scrollTo('billingAddressForm', SCROLL_OPTIONS);
    } else {
      const billingAddress = this.buildBillingAddressReq();
      if (billingAddress && this.paymentImplementation) {
        if (paymentMethod.impl === 'NIUBIZ_WEB') {
          this.setState({ niubizLoading: true });
        }
        this.paymentImplementation.executeUIAction(billingAddress, rootState);
      }
    }
  };

  buildSummaryData = (): ?{ [string]: string | ItemsByStore[] } => {
    const { order, bag, shoppingCart, forecast, selectedIndex, appliedCoupon, importMethod } = this.props;
    let coupon = (!!appliedCoupon && typeof selectedIndex === 'number' && !!importMethod &&
      !!forecast && !!forecast[importMethod]) ? {
      couponCode: appliedCoupon,
      couponDiscount: forecast[importMethod][selectedIndex].couponDiscount,
    } : undefined;

    if (this.pageMode === 'order' && order && order.invoice) {
      if (order.couponCode) {
        coupon = {
          couponCode: order.couponCode,
          couponDiscount: order.invoice.detail.coupon,
        };
      }
      return {
        weight: order.weightTotal,
        itemsByStores: groupItems(order.items),
        itemTotal: order.invoice.detail.itemTotal,
        subtotal: order.invoice.detail.subtotal,
        shippingTotal: order.invoice.detail.shipping,
        taxes: order.invoice.detail.taxes,
        importationFee: order.invoice.detail.importationFee,
        // services: order.invoice.detail.services,
        customsDuties: order.invoice.detail.customsDuties,
        qempo: order.invoice.detail.qempo,
        lastMileShipping: order.invoice.detail.lastMileShipping,
        ...coupon,
        total: order.invoice.detail.total,
        forecastType: order.state === 'CREATED_FOR_COURIER' ? 'DIY' : 'CLASSIC',
      }
    } else if (!!forecast && typeof selectedIndex === 'number' && !!importMethod &&
      ((this.pageMode === 'bag' && !!bag) || (this.pageMode === 'cart' && !!shoppingCart))) {
      let items: Item[] = [];
      if (this.pageMode === 'cart' && !!shoppingCart) {
        items = shoppingCart;
      } else if (this.pageMode === 'bag' && !!bag) {
        items = bag.items;
      }
      const weight = items.reduce((acum: number, item: Item) => acum + parseFloat(item.weight) * item.amount, 0)
        .toFixed(2);
      let f: ?(ForecastCourier | ForecastTier) = undefined;
      if (!!forecast && !!importMethod) {
        if (importMethod === 'CLASSIC') {
          f = forecast['CLASSIC'][selectedIndex];
        } else if (importMethod === 'DIY') {
          f = forecast['DIY'][selectedIndex];
        }
      }

      if (!!f) {
        return {
          weight,
          itemsByStores: groupItems(items),
          itemTotal: typeof f.itemTotal === 'string' ? f.itemTotal : '',
          taxes: typeof f.taxes === 'string' ? f.taxes : '',
          shippingTotal: typeof f.shipping === 'string' ? f.shipping : '',
          subtotal: typeof f.subtotal === 'string' ? f.subtotal : '',
          importationFee: typeof f.importationFee === 'string' ? f.importationFee : '',
          customsDuties: typeof f.customsDuties === 'string' ? f.customsDuties : '',
          qempo: typeof f.qempo === 'string' ? f.qempo : '',
          lastMileShipping: typeof f.lastMileShipping === 'string' ? f.lastMileShipping : '',
          ...coupon,
          total: forecast[importMethod][selectedIndex].total,
          forecastType: importMethod,
        };
      }
    }
    return undefined;
  };

  handleChangeTerms = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      this.setState((state: State) => ({
        acceptTerms: !state.acceptTerms,
      }))
    }
  }
  
  handleApplyCoupon = (couponCode: string) => {
    const {applyCoupon} = this.props;
    //More code...
    console.log('couponCode', couponCode);
    applyCoupon(couponCode);
  };

  handleRemoveCoupon = () => {
  };

  render() {
    const {
      locale, localValues, addresses, isFetchingPaymentMethods, paymentMethods, bag, order, shoppingCart,
      isPostingPaymentExecute, isCreatingOrderAndToken, isUpdatingOrderPaymentMethod,
      paymentExecuteUIError, isFetchingOrder, paymentGetLibraryError,
      forecast, importMethod, selectedIndex,
    } = this.props;

    const {
      deliveryAddress, addressOption, billingAddress, billingCountry,
      paymentMethod, paymentCaptureFailedModalOpen, acceptTerms, niubizLoading,
    } = this.state;
    const summaryData = this.buildSummaryData();
    /*const summary = <Fragment>
      {(!bag && !shoppingCart && !order && !summaryData) ?
        <QuoteSummaryLoading/> :
        summaryData && <QuoteSummary deliveryCountry={locale.domainCountry}
                                     weight={(summaryData.weight: any)}
                                     localValues={localValues}
                                     isLoading={false}
                                     subtotal={(summaryData.subtotal: any)}
                                     itemTotal={(summaryData.itemTotal: any)}
                                     shippingTotal={(summaryData.shippingTotal: any)}
                                     itemsByStores={(summaryData.itemsByStores: any)}
                                     taxes={(summaryData.taxes: any)}
                                     commission={(summaryData.commission: any)}
                                     services={(summaryData.services: any)}
                                     couponCode={(summaryData.couponCode: any)}
                                     couponDiscount={(summaryData.couponDiscount: any)}
                                     lastMileShipping={(summaryData.lastMileShipping: any)}
                                     total={(summaryData.total: any)}/>}
    </Fragment>;*/
    const localizedCountry = locale.domainCountry && getLocalizedCountry(locale.domainCountry,
      this.context.intl.formatMessage,
      localValues.countriesMap,
    );

    const isClassic = this.pageMode !== 'order' ? importMethod === 'CLASSIC' : (!!order && !!order.serviceTier);

    const summary = !!localizedCountry && !!summaryData ?
      <Summary titleIntl="summary.title.summary"
               initialOpen
               subtitleIntl="summary.subtitle.deliveryCountry"
               subtitleExtra={`: ${localizedCountry}`}>
        {!!isClassic &&
        <Fragment>
          <SummaryRow labelIntl="summary.detail.itemTotal"
                      content={`$ ${(summaryData.itemTotal: any) || ''}`}/>
          <SummaryRow labelIntl="summary.detail.taxes"
                      hintIntl="summary.detail.taxes.tooltip"
                      content={`$ ${(summaryData.taxes: any) || ''}`}/>
          <SummaryRow labelIntl="summary.detail.shipping"
                      hintIntl="summary.detail.shipping.tooltip"
                      content={`$ ${(summaryData.shippingTotal: any) || ''}`}/>
          <SummaryRule/>
        </Fragment>}
        {!!isClassic &&
        <SummaryRow labelIntl="summary.detail.subtotal"
                    noTopPadding
                    content={`$ ${(summaryData.subtotal: any) || ''}`}/>}
        <SummaryRow labelIntl="summary.detail.importationFee"
                    hintIntl="summary.detail.importationFee.tooltip"
                    content={`$ ${(summaryData.importationFee: any) || ''}`}/>
        {!!summaryData.customsDuties &&
        <SummaryRow labelIntl="summary.detail.customsDuties"
                    content={`$ ${(summaryData.customsDuties: any) || ''}`}/>}
        <SummaryRow labelIntl="summary.detail.qempo"
                    content={`$ ${(summaryData.qempo: any) || ''}`}/>
        <SummaryRow labelIntl="summary.detail.national"
                    hintIntl="summary.detail.national.tooltip"
                    content={`$ ${(summaryData.lastMileShipping: any) || ''}`}/>
        <FormattedMessage id="summary.detail.warranty.free">{
          txt => <SummaryRow labelIntl="summary.detail.warranty"
                             color="green"
                             content={txt}/>
        }</FormattedMessage>
        {!!summaryData.couponCode && !!summaryData.couponDiscount &&
        <SummaryRow labelIntl="summary.detail.coupon"
                    labelNoColon
                    labelExtra={`${(summaryData.couponCode: any)} :`}
                    color="lightBlue"
                    content={`$ ${(summaryData.couponDiscount: any) || ''}`}/>}
        <SummaryRow labelIntl="summary.detail.total"
                    color="black"
                    bold
                    content={`$ ${(summaryData.total: any) || ''}`}/>
      </Summary> :
      <Summary titleIntl="summary.title.summary"
               subtitleIntl="summary.subtitle.deliveryCountry"
               subtitleExtra={`: ${localizedCountry || ''}`}
               disabled/>;
    
    const couponBox = order && summaryData && (
      <div className={style.couponWrap}> 
        <Coupon
          appliedCouponCode={(summaryData.couponCode: any)}
          handleApplyCoupon={this.handleApplyCoupon}
          removeCoupon={this.handleRemoveCoupon}
        />
      </div>
    );

    const countryOptions: Option[] = (localValues.countries || DEFAULT_COUNTRIES).map((country) => ({
      value: country.key,
      image: country.icon,
      label: getLocalizedCountry(country.key, this.context.intl.formatMessage, localValues.countriesMap),
    }));
    const countryValidAddresses = (addresses || [])
      .filter((address: Address) => address.country.code === locale.domainCountry);
    // const addressQempo: AddressIntl = getQempoAddress(locale.domainCountry);
    const hideSameAddressOption: boolean = order &&
      order.deliveryCountry && order.deliveryCountry.code !== billingCountry.value;
    //   || deliveryMethod.value !== 'address';
    const errors: FormValidationResult[] = this.validateDeliveryAddressForm();
    return (
      <Fragment>
        <Helmet>
          <style type="text/css">{`
            #chat-bot-launcher-container {
              display: none!important;
            }            
          `}</style>
        </Helmet>
        <div className={style.checkoutStepWrap}>
          <div className={classNames(style.wrap, style.leftWrap)}>
            {/*deliveryMethod.value === 'address' && below html here*/}
            <section className={classNames(style.addressesOptions,
              { [style.loading]: isCreatingOrderAndToken || isUpdatingOrderPaymentMethod })}>
              <Element name="newAddressForm">
                <MyAddress handleChangeAdmin1={this.handleNewAddressChangeAdmin1}
                           handleChangeAdmin2={this.handleNewAddressChangeAdmin2}
                           handleChangeAdmin3={this.handleNewAddressChangeAdmin3}
                           handleChange={this.handleNewAddressChange}
                           handleChangeAddressOption={this.handleNewAddressChangeMyAddresses}
                           handleChangePhone={this.handleNewAddressChangePhone}
                           handleFocus={this.handleNewAddressFocus}
                           handleChangeSaveAddress={this.handleNewAddressChangeSaveAddress}
                           handlePhoneInit={this.handleNewAddressPhoneInit}
                           addresses={countryValidAddresses}
                           country={locale.domainCountry}
                           admin1={deliveryAddress.admin1}
                           admin1Items={deliveryAddress.admin1Items}
                           isLoadingAdmin1={deliveryAddress.isLoadingAdmin1}
                           admin2={deliveryAddress.admin2}
                           admin2Items={deliveryAddress.admin2Items}
                           isLoadingAdmin2={deliveryAddress.isLoadingAdmin2}
                           admin3={deliveryAddress.admin3}
                           admin3Items={deliveryAddress.admin3Items}
                           isLoadingAdmin3={deliveryAddress.isLoadingAdmin3}
                           address1={deliveryAddress.address1}
                           address2={deliveryAddress.address2}
                           firstName={deliveryAddress.firstName}
                           idNumber={deliveryAddress.idNumber}
                           lastName={deliveryAddress.lastName}
                           name={deliveryAddress.name}
                           phone={deliveryAddress.phone}
                           zipCode={deliveryAddress.zipCode}
                           saveAddress={deliveryAddress.saveAddress}
                           addressOption={addressOption}/>
              </Element>
            </section>
            {/*<section className={classNames(formStyle.formWrap,
              style.billingCountry, { [style.billingCountryDisabled]: errors.length || isCreatingOrderAndToken || isUpdatingOrderPaymentMethod })}>
              <FormattedMessage id="newOrder.checkout.billing.country.title">{
                txt => <h2 className={style.title}>2. {txt}</h2>
              }</FormattedMessage>
              <section className={classNames(formStyle.row, formStyle.inputRow, formStyle.rowNoMargin)}>
                <FormattedMessage id="newOrder.checkout.billing.country.label">{
                  txt => <label className={formStyle.label}>{txt}:</label>
                }</FormattedMessage>
                <Select options={countryOptions}
                        className={classNames({
                          'light-blue': billingCountry.value,
                          'red': (billingCountry: any).errors,
                        })}
                        value={billingCountry.value}
                        disabled={isCreatingOrderAndToken || isUpdatingOrderPaymentMethod}
                        placeholder={this.context.intl.formatMessage(messages.countryPlaceholder)}
                        handleChange={this.handleBillingAddressChangeCountry}/>
              </section>
            </section>*/}
            <PaymentMethod paymentMethod={paymentMethod}
                           deliveryAddressHasError={errors.length > 0}
                           isFetchingPaymentMethods={isFetchingPaymentMethods}
                           paymentMethods={paymentMethods}
                           paymentImplementation={this.paymentImplementation}
                           country={billingCountry}
                           isCreatingToken={isCreatingOrderAndToken || isUpdatingOrderPaymentMethod}
                           handleChangePaymentMethod={this.handleChangePaymentMethod}
                           paymentExecuteUIError={paymentExecuteUIError}
                           acceptTerms={acceptTerms}
                           handleChangeTerms={this.handleChangeTerms}/>
            {/*!!billingCountry.value && this.paymentMethodHasBillingAddress(paymentMethod.impl || '') &&
            <section className={classNames(style.billingAddressForm,
              { [style.billingAddressDisabled]: errors.length })}>
              <Element name="billingAddressForm">
                <BillingAddress handleBillingAddressUseSameAddress={this.handleBillingAddressUseSameAddress}
                                handleChangeAdmin1={this.handleBillingAddressChangeAdmin1}
                                handleChangeAdmin2={this.handleBillingAddressChangeAdmin2}
                                handleChangeAdmin3={this.handleBillingAddressChangeAdmin3}
                                handleChange={this.handleBillingAddressChange}
                                handleChangePhone={this.handleBillingAddressChangePhone}
                                handleFocus={this.handleBillingAddressFocus}
                                handlePhoneInit={this.handleBillingAddressPhoneInit}
                                addresses={addresses}
                                country={billingCountry.value}
                                admin1={billingAddress.admin1}
                                admin1Items={billingAddress.admin1Items}
                                isLoadingAdmin1={billingAddress.isLoadingAdmin1}
                                admin2={billingAddress.admin2}
                                admin2Items={billingAddress.admin2Items}
                                isLoadingAdmin2={billingAddress.isLoadingAdmin2}
                                admin3={billingAddress.admin3}
                                admin3Items={billingAddress.admin3Items}
                                isLoadingAdmin3={billingAddress.isLoadingAdmin3}
                                address1={billingAddress.address1}
                                address2={billingAddress.address2}
                                firstName={billingAddress.firstName}
                                idNumber={billingAddress.idNumber}
                                lastName={billingAddress.lastName}
                                name={billingAddress.name}
                                phone={billingAddress.phone}
                                zipCode={billingAddress.zipCode}
                                useSameAddress={billingAddress.useSameAddress}
                                hideSameAddressOption={hideSameAddressOption}/>
              </Element>
            </section>*/}
          </div>
          <div className={classNames(style.wrap, style.rightWrap)}>
            <section className={style.rightSection}>
              {summary}
              {couponBox}
            </section>
            {(!paymentMethod.impl || (!!paymentMethod.impl && (
              paymentMethod.impl === 'BLUESNAP' || paymentMethod.impl === 'CULQI' ||
              paymentMethod.impl === 'SAFETYPAY' || paymentMethod.impl === 'KASHIO' ||
              paymentMethod.impl === 'PAYME_USD' || paymentMethod.impl === 'NIUBIZ_DECOUPLED' ||
              paymentMethod.impl === 'NIUBIZ_WEB'))) &&
            <FinalPrice total={!!summaryData && typeof summaryData.total === 'string' ? summaryData.total : ''}
                        forecastType={!!summaryData && typeof summaryData.forecastType === 'string' ?
                          (summaryData: any).forecastType : 'DIY'}
                        disabled={!paymentMethod.impl || isCreatingOrderAndToken ||
                        isUpdatingOrderPaymentMethod || isPostingPaymentExecute ||
                        (paymentMethod.value === 'CARD' && !acceptTerms)}
                        handleClick={(e: Event) => this.handleConfirmPayment()}/>}
            {/*<section className={style.hint}>
              <i className={icons.iconCheckCircleMedium}/>
              <FormattedMessage id="newOrder.checkout.hint">{
                text => <p>{text}</p>
              }</FormattedMessage>
            </section>*/}
          </div>
          <Modal handleClose={() => this.setState({ paymentCaptureFailedModalOpen: false })}
                 open={paymentCaptureFailedModalOpen}
                 showCloseButton={true}
                 big>
            <section className={style.errorModalWrap}>
              <FormattedMessage id="newOrder.checkout.modal.paymentCaptureFailedModal.title">{
                txt => <h3 className={style.errorModalTitle}>{txt}</h3>
              }</FormattedMessage>
              <p className={style.errorModalText}>
                <FormattedMessage id="newOrder.checkout.modal.paymentCaptureFailedModal.text.first">{
                  txt => txt
                }</FormattedMessage><br/>
                <FormattedMessage id="newOrder.checkout.modal.paymentCaptureFailedModal.text.second">{
                  txt => txt
                }</FormattedMessage>
              </p>
              <FormattedMessage id="newOrder.checkout.modal.paymentCaptureFailedModal.button">{
                txt => <button
                  onClick={() => this.setState({ paymentCaptureFailedModalOpen: false })}
                  className={classNames(style.errorModalButton, buttonStyle.btn, buttonStyle.darkGrey)}>{txt}</button>
              }</FormattedMessage>
            </section>
          </Modal>
          <Modal handleClose={() => undefined}
                 open={isPostingPaymentExecute || isFetchingOrder || niubizLoading}
                 transparent
                 maskColor="white"
                 showCloseButton={false}>
            <Loading type="circle"
                     className={style.loading}/>
          </Modal>
        </div>
      </Fragment>
    );
  }
}

CheckoutStep.propTypes = {
  dispatch: PropTypes.func.isRequired,
  rootState: PropTypes.object.isRequired,
  locale: PropTypes.object.isRequired,
  fetchAddresses: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  location: PropTypes.object,
  addresses: PropTypes.array,
  isFetchingAddresses: PropTypes.bool,
  fetchAddressesError: PropTypes.object,
  paymentMethods: PropTypes.object,
  isFetchingPaymentMethods: PropTypes.bool,
  fetchPaymentMethodsError: PropTypes.object,
  fetchPaymentMethods: PropTypes.func.isRequired,
  order: PropTypes.object,
  isFetchingOrder: PropTypes.bool,
  fetchOrderError: PropTypes.object,
  isCreatingOrderAndToken: PropTypes.bool,
  createOrderAndTokenError: PropTypes.object,
  isUpdatingOrderPaymentMethod: PropTypes.bool,
  updateOrderPaymentMethodError: PropTypes.object,
  createCartOrderAndToken: PropTypes.func.isRequired,
  createBagOrderAndToken: PropTypes.func.isRequired,
  updateOrderPaymentMethod: PropTypes.func.isRequired,
  fetchOrder: PropTypes.func.isRequired,
  goToRedirect: PropTypes.func.isRequired,
  bag: PropTypes.object,
  isPostingPaymentExecute: PropTypes.bool,
  paymentExecuteSuccess: PropTypes.bool,
  paymentExecuteError: PropTypes.object,
  postPaymentExecute: PropTypes.func.isRequired,
  resetState: PropTypes.func.isRequired,
  isFetchingCart: PropTypes.bool,
  shoppingCart: PropTypes.array,
  importMethod: PropTypes.string,
  selectedIndex: PropTypes.number,
  forecast: PropTypes.object,
  appliedCoupon: PropTypes.string,
  removeAppliedCoupon: PropTypes.func.isRequired,
  paymentExecuteUIError: PropTypes.object,
  paymentGetLibraryError: PropTypes.object,
  resetAlertState: PropTypes.func.isRequired,
  postPaymentExecuteSuccess: PropTypes.func.isRequired,
  applyCoupon: PropTypes.func.isRequired,
};

CheckoutStep.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/new-order/CheckoutStep/components/CheckoutStep.jsx